import { IBreakdownOverallStatus, MaybeNil } from '../../../utils/interfaces';
import { FunnelModeKey } from '../../../utils/apollo/graphqlSchema';
import { IChartLine } from '../hooks/useChartLines';
import { IParsedChartDataFunnel } from '../interfaces';
import { assertCondition, EmptyObject } from '../../../utils/types';
import { findByKey } from '../../../utils/redux/util';
import { getFunnelSliceModeMeasures } from '../utils/funnel';
import { IAnalysisColorConfig } from '../utils/analysisColorConfig';
import { isLastStepInFreqOrTTCWhichSkipMiddleSteps } from '../FunnelChart/utils';

export enum FunnelTimeToConvertStatType {
  MEDIAN = 1,
  AVERAGE,
}

interface ISliceInfo {
  sliceName: string;
}

export interface IFunnelTimeToConvertStatistics extends Partial<ISliceInfo> {
  key: string;
  type: FunnelTimeToConvertStatType;
  lineColor: string;
  valueColor: string;
  value: MaybeNil<number>;
}

export const getFunnelTimeToConvertStatistics = (
  visibleLines: IChartLine[],
  chartData: IParsedChartDataFunnel,
  breakdownStatus: IBreakdownOverallStatus,
  colorConfig: IAnalysisColorConfig,
): IFunnelTimeToConvertStatistics[] | null => {
  if (chartData.mode !== FunnelModeKey.TimeToConvert) {
    return null;
  }

  assertCondition(chartData.xAxis?.__typename === 'FunnelTimeToConvertXAxis');

  const result: IFunnelTimeToConvertStatistics[] = [];
  const checkIsUsingLastStepColorInTimeToConvert = (
    firstStepKey: string,
    lastStepKey: string,
  ) => {
    const funnelModeSlices = chartData.funnelModeSlices;
    const foundStep = funnelModeSlices.find(
      (step) =>
        step.firstMeasureKey === firstStepKey &&
        step.lastMeasureKey === lastStepKey,
    );

    return (
      foundStep &&
      isLastStepInFreqOrTTCWhichSkipMiddleSteps(foundStep.measureSelection)
    );
  };
  for (const vl of visibleLines) {
    const column = findByKey(chartData.columns, vl.name);

    const [firstMeasure, lastMeasure] = getFunnelSliceModeMeasures(
      chartData.linesMeta[vl.name],
      chartData.measureControls,
      colorConfig,
    );

    const isUsingLastStepColor = checkIsUsingLastStepColorInTimeToConvert(
      firstMeasure.measureKey,
      lastMeasure.measureKey,
    );

    let sliceInfo: ISliceInfo | EmptyObject = {};
    if (breakdownStatus !== IBreakdownOverallStatus.NoBreakdown) {
      sliceInfo = {
        sliceName: column.sliceName,
      };
    }

    result.push({
      key: column.key,
      lineColor: vl.color,
      valueColor: isUsingLastStepColor ? lastMeasure.color : firstMeasure.color,
      type: FunnelTimeToConvertStatType.AVERAGE,
      value: column.statistics?.avg,
      ...sliceInfo,
    });
    result.push({
      key: column.key,
      lineColor: vl.color,
      valueColor: isUsingLastStepColor ? lastMeasure.color : firstMeasure.color,
      type: FunnelTimeToConvertStatType.MEDIAN,
      value: column.statistics?.median,
      ...sliceInfo,
    });
  }

  return result;
};
